import type { Event } from '@hvf/contracts'
import { useEvents } from '@state/events'
import dayjs from 'dayjs'
import { createMemo, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { getOccurrences } from './date-utils'
import type { DateRange, EventOccurrence } from './date-utils'

export function useCalendarState() {
	const { events } = useService(useEvents)()

	const [viewDate, setViewDate] = createSignal(dayjs())
	const [activeEvent, setActiveEvent] = createSignal<EventOccurrence>()
	// Calendar-page filters. They only narrow the calendar grid; upcomingEvents (dashboard) ignores them.
	const [search, setSearch] = createSignal('')
	const [groupFilter, setGroupFilter] = createSignal<string | null>(null)

	const matchesFilters = (event: Event) => {
		const q = search().trim().toLowerCase()
		if (groupFilter() && (event.group ?? '') !== groupFilter()) return false
		if (q && !`${event.group ?? ''} ${event.description ?? ''}`.toLowerCase().includes(q)) return false
		return true
	}

	/** Distinct event groups, for the group filter */
	const eventGroups = createMemo(() =>
		[...new Set((events.data ?? []).map((e) => e.group).filter((g): g is string => !!g))].sort(),
	)

	/**
	 * Jump the calendar to an event and open it — used by global search links (/events?event=<id>).
	 * Picks the next occurrence within a year, falling back to the most recent past one.
	 */
	const focusEvent = (eventId: string) => {
		const event = (events.data ?? []).find((e) => e.id === eventId)
		if (!event) return false
		const now = dayjs()
		const upcoming = getOccurrences(event, { start: now.toDate(), end: now.add(1, 'year').toDate() })
			.filter((o) => !o.localTime.isBefore(now))
			.sort((a, b) => a.localTime.diff(b.localTime))
		const occurrence =
			upcoming[0] ??
			getOccurrences(event, { start: now.subtract(1, 'year').toDate(), end: now.toDate() }).sort((a, b) =>
				b.localTime.diff(a.localTime),
			)[0]
		if (!occurrence) return false
		setViewDate(occurrence.localTime)
		setActiveEvent(occurrence)
		return true
	}

	const actions = {
		nextMonth: () => setViewDate((prev) => prev.add(1, 'month')),
		prevMonth: () => setViewDate((prev) => prev.subtract(1, 'month')),
		today: () => setViewDate(dayjs()),
		setActiveEvent,
		setSearch,
		setGroupFilter,
		focusEvent,
		clearFilters: () => {
			setSearch('')
			setGroupFilter(null)
		},
	}

	const range = createMemo<DateRange>(() => {
		const startOfMonth = viewDate().startOf('month')
		const startOffset = startOfMonth.day()

		return {
			// Start fetching from the first visible day on the calendar grid
			start: startOfMonth.subtract(startOffset, 'day').toDate(),
			// Add some padding to the end of the month to catch the next month's visible days
			end: viewDate().endOf('month').add(7, 'day').toDate(),
		}
	})

	const groupedEvents = createMemo<Map<string, EventOccurrence[]>>(() => {
		const eventMap = new Map<string, EventOccurrence[]>()
		const currentRange = range()
		const data: Event[] = events.data ?? []

		for (const event of data) {
			const occurrences = getOccurrences(event, currentRange)

			for (const occ of occurrences) {
				const dateKey = occ.localTime.format('YYYY-MM-DD')
				const list = eventMap.get(dateKey) ?? []
				list.push(occ)
				eventMap.set(dateKey, list)
			}
		}

		return eventMap
	})

	const days = createMemo(() => {
		const startOfMonth = viewDate().startOf('month')

		// day() returns 0 for Sun, 1 for Mon, etc.
		const startOffset = startOfMonth.day()

		const daysInMonth = viewDate().daysInMonth()

		// Calculate how many days we need to add to the end to complete the final week
		const totalDaysSoFar = startOffset + daysInMonth
		const endOffset = totalDaysSoFar % 7 === 0 ? 0 : 7 - (totalDaysSoFar % 7)

		const totalGridDays = startOffset + daysInMonth + endOffset

		const currentEventsMap = groupedEvents()
		const filtering = !!search().trim() || !!groupFilter()
		const today = dayjs()

		return Array.from({ length: totalGridDays }, (_, i) => {
			// By subtracting the startOffset, we shift our starting point back into the previous month
			const currentDay = startOfMonth.subtract(startOffset, 'day').add(i, 'day')
			const dateStr = currentDay.format('YYYY-MM-DD')

			return {
				date: currentDay,
				isCurrentMonth: currentDay.isSame(viewDate(), 'month'), // This is dynamically true/false now!
				isToday: currentDay.isSame(today, 'day'),
				events: (currentEventsMap.get(dateStr) ?? []).filter((occ) => !filtering || matchesFilters(occ)),
			}
		})
	})

	const upcomingEvents = createMemo<EventOccurrence[]>(() => {
		const now = dayjs()
		const allOccurrences: EventOccurrence[] = []

		// Flatten all occurrences from the map into a single array
		for (const occurrences of groupedEvents().values()) {
			allOccurrences.push(...occurrences)
		}

		return (
			allOccurrences
				// 1. Only include events starting now or in the future
				.filter((occ) => occ.localTime.isAfter(now) || occ.localTime.isSame(now))
				// 2. Sort by time ascending (soonest first)
				.sort((a, b) => a.localTime.diff(b.localTime))
				// 3. Take the top 3
				.slice(0, 3)
		)
	})

	return {
		activeEvent,
		viewDate,
		days,
		upcomingEvents,
		search,
		groupFilter,
		eventGroups,
		...actions,
	}
}
